/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WASP Frontend v2.4.0 - useScheduler Hook
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file useScheduler.ts
 * @description Hook para gestión de envíos programados
 * @version 1.0.0
 * @date 2025-01-27
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  ScheduledMessage,
  ScheduleFilters,
  SchedulerStats
} from '../types/scheduler.types';
import { useUserContext } from '../layouts/UserContext';

// ═══════════════════════════════════════════════════════════════════════════
// API CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════

const API_BASE_URL = 'https://backend.anubisai.net/api/whatsapp/wasp';
const API_KEY = 'anubis-whatsapp-api-key';

// Get auth token
const getAuthToken = (): string => {
  try {
    const token = localStorage.getItem('hatateToken');
    return token || '';
  } catch (e) {
    console.error('Error getting auth token:', e);
    return '';
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

interface UseSchedulerReturn {
  // Data
  schedules: ScheduledMessage[];
  stats: SchedulerStats | null;
  
  // State
  isLoading: boolean;
  error: string | null;
  
  // Filters
  filters: ScheduleFilters;
  setFilters: (filters: ScheduleFilters) => void;
  
  // CRUD Operations
  createSchedule: (data: Partial<ScheduledMessage>) => Promise<ScheduledMessage>;
  updateSchedule: (id: string, data: Partial<ScheduledMessage>) => Promise<ScheduledMessage>;
  cancelSchedule: (id: string) => Promise<void>;
  deleteSchedule: (id: string) => Promise<void>;
  getSchedule: (id: string) => Promise<ScheduledMessage | null>;
  
  // Actions
  refreshSchedules: () => Promise<void>;
  refreshStats: () => Promise<void>;
}

// ═══════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════

export const useScheduler = (): UseSchedulerReturn => {
  // ═══════════════════════════════════════════════════════════════════════
  // CONTEXT
  // ═══════════════════════════════════════════════════════════════════════
  
  const { user } = useUserContext();
  const phoneId = user?._id || '';
  
  // ═══════════════════════════════════════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════════════════════════════════════
  
  const [schedules, setSchedules] = useState<ScheduledMessage[]>([]);
  const [stats, setStats] = useState<SchedulerStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ScheduleFilters>({});

  // ═══════════════════════════════════════════════════════════════════════
  // LOAD SCHEDULES
  // ═══════════════════════════════════════════════════════════════════════
  
  const loadSchedules = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const token = getAuthToken();
      
      if (!phoneId) {
        throw new Error('Usuario no autenticado. Por favor inicia sesión nuevamente.');
      }
      
      console.log('📡 Cargando schedules desde API...');
      console.log('  - phoneId:', phoneId);
      
      const response = await axios.get(`${API_BASE_URL}/scheduler/schedules`, {
        params: { 
          phoneId,
          ...filters 
        },
        headers: {
          'x-api-key': API_KEY,
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('✅ Schedules cargados:', response.data);
      
      if (response.data.success) {
        setSchedules(response.data.schedules || []);
      } else {
        throw new Error(response.data.message || 'Error al cargar schedules');
      }
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Error al cargar envíos programados';
      setError(message);
      console.error('❌ Error loading schedules:', err);
      console.error('  - Status:', err.response?.status);
      console.error('  - Data:', err.response?.data);
    } finally {
      setIsLoading(false);
    }
  }, [filters, phoneId]);

  // ═══════════════════════════════════════════════════════════════════════
  // LOAD STATS
  // ═══════════════════════════════════════════════════════════════════════
  
  const loadStats = useCallback(async () => {
    try {
      const token = getAuthToken();
      
      if (!phoneId) return;
      
      console.log('📊 Cargando stats desde API...');
      
      const response = await axios.get(`${API_BASE_URL}/scheduler/stats`, {
        params: { phoneId },
        headers: {
          'x-api-key': API_KEY,
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('📊 Response stats:', response.data);
      
      if (response.data.success) {
        console.log('✅ Stats recibidos:', response.data.stats);
        setStats(response.data.stats);
      } else {
        console.log('⚠️ Backend retornó success: false');
      }
    } catch (err) {
      console.error('❌ Error loading stats:', err);
      // Sin fallback para evitar dependencias circulares
    }
  }, [phoneId]);

  // ═══════════════════════════════════════════════════════════════════════
  // CREATE SCHEDULE
  // ═══════════════════════════════════════════════════════════════════════
  
  const createSchedule = useCallback(async (data: Partial<ScheduledMessage>): Promise<ScheduledMessage> => {
    try {
      setError(null);
      
      const token = getAuthToken();
      
      if (!phoneId) {
        throw new Error('Usuario no autenticado. Por favor inicia sesión nuevamente.');
      }
      
      console.log('📡 Creando schedule en API...');
      console.log('  - phoneId:', phoneId);
      console.log('  - Data:', data);
      
      const response = await axios.post(
        `${API_BASE_URL}/scheduler/schedules`,
        {
          ...data,
          phoneId
        },
        {
          headers: {
            'x-api-key': API_KEY,
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('✅ Schedule creado:', response.data);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Error al crear schedule');
      }
      
      const newSchedule = response.data.schedule;
      
      // Refrescar lista y stats
      await loadSchedules();
      await loadStats();
      
      return newSchedule;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Error al crear envío programado';
      setError(message);
      console.error('❌ Error creating schedule:', err);
      console.error('  - Status:', err.response?.status);
      console.error('  - Data:', err.response?.data);
      throw err;
    }
  }, [loadSchedules, loadStats, phoneId]);

  // ═══════════════════════════════════════════════════════════════════════
  // UPDATE SCHEDULE
  // ═══════════════════════════════════════════════════════════════════════
  
  const updateSchedule = useCallback(async (
    id: string,
    data: Partial<ScheduledMessage>
  ): Promise<ScheduledMessage> => {
    try {
      setError(null);
      
      
      const token = getAuthToken();
      
      if (!phoneId) {
        throw new Error('Usuario no autenticado. Por favor inicia sesión nuevamente.');
      }
      
      console.log('📡 Actualizando schedule:', id);
      
      const response = await axios.put(
        `${API_BASE_URL}/scheduler/schedules/${id}`,
        {
          ...data,
          phoneId
        },
        {
          headers: {
            'x-api-key': API_KEY,
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Error al actualizar schedule');
      }
      
      const updated = response.data.schedule;
      
      // Actualizar en la lista local
      setSchedules(prev => prev.map(s => s.id === id ? updated : s));
      
      return updated;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Error al actualizar envío';
      setError(message);
      console.error('❌ Error updating schedule:', err);
      throw err;
    }
  }, [phoneId]);

  // ═══════════════════════════════════════════════════════════════════════
  // CANCEL SCHEDULE
  // ═══════════════════════════════════════════════════════════════════════
  
  const cancelSchedule = useCallback(async (id: string): Promise<void> => {
    try {
      setError(null);
      
      
      const token = getAuthToken();
      
      if (!phoneId) {
        throw new Error('Usuario no autenticado. Por favor inicia sesión nuevamente.');
      }
      
      console.log('📡 Cancelando schedule:', id);
      
      const response = await axios.post(
        `${API_BASE_URL}/scheduler/schedules/${id}/cancel`,
        { phoneId },
        {
          headers: {
            'x-api-key': API_KEY,
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Error al cancelar schedule');
      }
      
      // Actualizar en la lista local
      setSchedules(prev => prev.map(s => 
        s.id === id 
          ? { 
              ...s, 
              status: 'cancelled',
              metadata: {
                ...s.metadata,
                cancelledAt: new Date().toISOString(),
                cancelledBy: phoneId
              }
            }
          : s
      ));
      
      // Refrescar stats
      await loadStats();
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Error al cancelar envío';
      setError(message);
      console.error('❌ Error cancelling schedule:', err);
      throw err;
    }
  }, [loadStats, phoneId]);

  // ═══════════════════════════════════════════════════════════════════════
  // DELETE SCHEDULE
  // ═══════════════════════════════════════════════════════════════════════
  
  const deleteSchedule = useCallback(async (id: string): Promise<void> => {
    try {
      setError(null);
      
      
      const token = getAuthToken();
      
      if (!phoneId) {
        throw new Error('Usuario no autenticado. Por favor inicia sesión nuevamente.');
      }
      
      console.log('📡 Eliminando schedule:', id);
      
      const response = await axios.delete(`${API_BASE_URL}/scheduler/schedules/${id}`, {
        params: { phoneId },
        headers: {
          'x-api-key': API_KEY,
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Error al eliminar schedule');
      }
      
      // Eliminar de la lista local
      setSchedules(prev => prev.filter(s => s.id !== id));
      
      // Refrescar stats
      await loadStats();
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Error al eliminar envío';
      setError(message);
      console.error('❌ Error deleting schedule:', err);
      throw err;
    }
  }, [loadStats, phoneId]);

  // ═══════════════════════════════════════════════════════════════════════
  // GET SCHEDULE
  // ═══════════════════════════════════════════════════════════════════════
  
  const getSchedule = useCallback(async (id: string): Promise<ScheduledMessage | null> => {
    try {
      setError(null);
      
      
      const token = getAuthToken();
      
      if (!phoneId) {
        throw new Error('Usuario no autenticado. Por favor inicia sesión nuevamente.');
      }
      
      const response = await axios.get(`${API_BASE_URL}/scheduler/schedules/${id}`, {
        params: { phoneId },
        headers: {
          'x-api-key': API_KEY,
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Error al obtener schedule');
      }
      
      return response.data.schedule;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Error al obtener envío';
      setError(message);
      console.error('❌ Error getting schedule:', err);
      return null;
    }
  }, [phoneId]);

  // ═══════════════════════════════════════════════════════════════════════
  // REFRESH
  // ═══════════════════════════════════════════════════════════════════════
  
  const refreshSchedules = useCallback(async () => {
    await loadSchedules();
  }, [loadSchedules]);

  const refreshStats = useCallback(async () => {
    await loadStats();
  }, [loadStats]);

  // ═══════════════════════════════════════════════════════════════════════
  // EFFECTS
  // ═══════════════════════════════════════════════════════════════════════
  
  // Load initial data
  useEffect(() => {
    loadSchedules();
    loadStats();
  }, [loadSchedules, loadStats]);

  // ═══════════════════════════════════════════════════════════════════════
  // RETURN
  // ═══════════════════════════════════════════════════════════════════════
  
  return {
    // Data
    schedules,
    stats,
    
    // State
    isLoading,
    error,
    
    // Filters
    filters,
    setFilters,
    
    // CRUD
    createSchedule,
    updateSchedule,
    cancelSchedule,
    deleteSchedule,
    getSchedule,
    
    // Actions
    refreshSchedules,
    refreshStats
  };
};

export default useScheduler;